import React, { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import styled from 'styled-components';
import AuthModal from './AuthModal';
import { useAuth } from '../auth/AuthContext';
import {
  REVIEW_MAX_LENGTH,
  REVIEW_MODERATOR_UID,
  clearReview,
  deleteRating,
  fetchPublicReviews,
  saveRating,
  saveReview,
  subscribeMyRating,
  subscribeRatingStats
} from '../services/ratings';

const Wrap = styled.div`
  margin-top: ${(p) => (p.$compact ? '6px' : '10px')};
  pointer-events: auto;
  position: relative;
  z-index: 3;
`;

const Summary = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  align-items: baseline;
  color: #fff;
  font-size: ${(p) => (p.$compact ? '0.75rem' : '0.95rem')};
  font-weight: 700;
  text-shadow: ${(p) => (p.$compact ? '0 1px 4px rgba(0,0,0,0.8)' : 'none')};
`;

const Count = styled.span`
  font-weight: 500;
  color: rgba(255, 255, 255, 0.75);
`;

const StarsRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px 12px;
  margin-top: 2px;
`;

const Stars = styled.div`
  display: flex;
  gap: 1px;
`;

const ClearRating = styled.button`
  border: 0;
  background: transparent;
  color: rgba(255, 255, 255, 0.7);
  font: inherit;
  font-size: ${(p) => (p.$compact ? '0.7rem' : '0.8rem')};
  text-decoration: underline;
  cursor: pointer;
  padding: 0;
  text-shadow: ${(p) => (p.$compact ? '0 1px 4px rgba(0,0,0,0.8)' : 'none')};

  &:disabled {
    cursor: default;
  }
`;

const Star = styled.button`
  border: 0;
  background: transparent;
  color: ${(p) => (p.$on ? '#f5d76e' : 'rgba(255,255,255,0.72)')};
  font-size: ${(p) => (p.$compact ? '1rem' : '1.55rem')};
  line-height: 1;
  padding: 0 1px;
  cursor: pointer;

  &:disabled {
    cursor: default;
  }
`;

const Hint = styled.p`
  margin: 4px 0 0;
  font-size: 0.72rem;
  color: rgba(255, 255, 255, 0.7);
`;

const ReviewForm = styled.form`
  margin-top: 12px;
`;

const ReviewLabel = styled.label`
  display: block;
  margin-bottom: 6px;
  color: rgba(255, 255, 255, 0.85);
  font-size: 0.85rem;
`;

const ReviewInput = styled.textarea`
  width: 100%;
  min-height: 72px;
  resize: vertical;
  border-radius: 10px;
  border: 1px solid rgba(255, 255, 255, 0.2);
  background: rgba(0, 0, 0, 0.25);
  color: #fff;
  font: inherit;
  font-size: 0.9rem;
  padding: 10px 12px;
`;

const ReviewActions = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-top: 6px;
`;

const Counter = styled.span`
  color: rgba(255, 255, 255, 0.6);
  font-size: 0.75rem;
`;

const SavedReview = styled.div`
  margin-top: 12px;
`;

const SavedMessage = styled.p`
  margin: 0 0 8px;
  color: #b8f5c8;
  font-weight: 700;
  font-size: 0.9rem;
`;

const EditReviewButton = styled.button`
  margin-top: 10px;
  border: 1px solid rgba(255, 255, 255, 0.35);
  background: transparent;
  color: #fff;
  border-radius: 999px;
  padding: 8px 14px;
  font: inherit;
  font-weight: 600;
  cursor: pointer;
`;

const SaveReviewButton = styled.button`
  border: 0;
  border-radius: 999px;
  padding: 8px 16px;
  font: inherit;
  font-weight: 700;
  color: #fff;
  cursor: pointer;
  background: linear-gradient(90deg, #e040c8, #8b5cf6);

  &:disabled {
    opacity: 0.5;
    cursor: default;
  }
`;

const PublicNote = styled.p`
  margin: 8px 0 0;
  color: rgba(255, 255, 255, 0.7);
  font-size: 0.8rem;
  line-height: 1.4;

  a {
    color: #d7c4ff;
  }
`;

const ReviewCard = styled.article`
  margin-top: ${(p) => (p.$compact ? '8px' : '14px')};
  padding-top: ${(p) => (p.$compact ? '8px' : '12px')};
  border-top: 1px solid rgba(255, 255, 255, 0.12);
`;

const ReviewMeta = styled.p`
  margin: 0 0 6px;
  color: rgba(255, 255, 255, 0.7);
  font-size: 0.8rem;
`;

const ReviewBody = styled.p`
  margin: 0;
  white-space: pre-wrap;
  line-height: 1.45;
`;

const ConfirmOverlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 3000;
  background: rgba(5, 0, 10, 0.7);
  backdrop-filter: blur(6px);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
`;

const ConfirmCard = styled.div`
  width: min(400px, 100%);
  background: rgba(20, 8, 36, 0.96);
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 18px;
  padding: 24px;
  color: #fff;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.5);
`;

const ConfirmMessage = styled.p`
  margin: 0 0 18px;
  font-size: 1.05rem;
  font-weight: 700;
  line-height: 1.4;
`;

const ConfirmActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 10px;
`;

const ConfirmCancel = styled.button`
  border: 1px solid rgba(255, 255, 255, 0.35);
  background: transparent;
  color: #fff;
  border-radius: 999px;
  padding: 8px 16px;
  font: inherit;
  font-weight: 600;
  cursor: pointer;
`;

const ConfirmClear = styled.button`
  border: 0;
  border-radius: 999px;
  padding: 8px 16px;
  font: inherit;
  font-weight: 700;
  color: #fff;
  cursor: pointer;
  background: linear-gradient(90deg, #e040c8, #8b5cf6);

  &:disabled {
    opacity: 0.5;
    cursor: default;
  }
`;

const RemoveButton = styled.button`
  margin-top: 8px;
  border: 0;
  background: transparent;
  color: #ff8aa0;
  font: inherit;
  font-size: 0.8rem;
  cursor: pointer;
  padding: 0;
`;

const MoreButton = styled.button`
  margin-top: 12px;
  border: 1px solid rgba(255, 255, 255, 0.3);
  background: transparent;
  color: #fff;
  border-radius: 999px;
  padding: 8px 14px;
  font: inherit;
  cursor: pointer;
`;

const SignInButton = styled.button`
  margin-top: 6px;
  border: 1px solid rgba(255, 255, 255, 0.35);
  background: transparent;
  color: #fff;
  border-radius: 999px;
  padding: 6px 12px;
  font: inherit;
  font-size: ${(p) => (p.$compact ? '0.7rem' : '0.85rem')};
  font-weight: 600;
  cursor: pointer;
`;

const PAGE_SIZE = 10;

function timestampMillis(value) {
  if (!value) return 0;
  if (typeof value.toMillis === 'function') return value.toMillis();
  if (typeof value.seconds === 'number') return value.seconds * 1000;
  return 0;
}

function reviewWasEdited(review) {
  const created = timestampMillis(review.createdAt);
  const updated = timestampMillis(review.updatedAt);
  return created > 0 && updated > 0 && created !== updated;
}

function formatReviewDate(value) {
  const ms = timestampMillis(value);
  if (!ms) return '';
  return new Date(ms).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });
}

function ExperienceRating({ experienceId, compact = false, showReviews = false, summaryOnly = false }) {
  const { user } = useAuth();
  const [stats, setStats] = useState({ count: 0, total: 0, average: 0 });
  const [mine, setMine] = useState(null);
  const [authOpen, setAuthOpen] = useState(false);
  const [authMode, setAuthMode] = useState('login');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [draft, setDraft] = useState('');
  const [draftReady, setDraftReady] = useState(false);
  const [reviewEditing, setReviewEditing] = useState(false);
  const [reviewSaved, setReviewSaved] = useState(false);
  const [confirmedText, setConfirmedText] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  const [clearConfirmOpen, setClearConfirmOpen] = useState(false);

  const reloadReviews = useCallback(async () => {
    if (!showReviews || !experienceId) return;
    try {
      setReviews(await fetchPublicReviews(experienceId));
    } catch (err) {
      setReviews([]);
    }
  }, [experienceId, showReviews]);

  useEffect(() => {
    if (!experienceId) return undefined;
    return subscribeRatingStats(experienceId, setStats);
  }, [experienceId]);

  useEffect(() => {
    if (!experienceId || !user?.uid) {
      setMine(null);
      return undefined;
    }
    return subscribeMyRating(user.uid, experienceId, setMine);
  }, [experienceId, user?.uid]);

  useEffect(() => {
    setDraftReady(false);
    setDraft('');
    setVisibleCount(PAGE_SIZE);
    setReviewEditing(false);
    setReviewSaved(false);
    setConfirmedText(null);
    setClearConfirmOpen(false);
  }, [experienceId, user?.uid]);

  useEffect(() => {
    if (!mine || draftReady) return;
    setDraft(typeof mine.reviewText === 'string' ? mine.reviewText : '');
    setDraftReady(true);
  }, [mine, draftReady]);

  useEffect(() => {
    reloadReviews();
  }, [reloadReviews]);

  useEffect(() => {
    const name = user?.displayName && String(user.displayName).trim();
    if (!user?.uid || !name) return;
    const prefix = `${user.uid}_`;
    setReviews((prev) => {
      let changed = false;
      const next = prev.map((review) => {
        if (!review.id || !String(review.id).startsWith(prefix) || review.authorName === name) return review;
        changed = true;
        return { ...review, authorName: name };
      });
      return changed ? next : prev;
    });
  }, [user?.uid, user?.displayName]);

  if (!experienceId) return null;

  const count = stats.count || 0;
  const summary = count > 0 ? `★ ${Number(stats.average).toFixed(1)}` : 'No ratings yet';
  const countLabel = count === 1 ? '1 rating' : `${count} ratings`;

  if (summaryOnly) {
    return (
      <Wrap $compact={compact}>
        <Summary $compact={compact}>
          <span>{summary}</span>
          {count > 0 && <Count>{countLabel}</Count>}
        </Summary>
      </Wrap>
    );
  }

  const savedReviewText = confirmedText != null
    ? confirmedText
    : (typeof mine?.reviewText === 'string' ? mine.reviewText : '');
  const hasWrittenReview = !!savedReviewText.trim();

  const removeMyRating = async () => {
    await deleteRating(user.uid, experienceId);
    setDraft('');
    setDraftReady(false);
    setReviewEditing(false);
    setReviewSaved(false);
    setConfirmedText(null);
    setClearConfirmOpen(false);
  };

  const performClearRating = async () => {
    if (!user || !mine?.stars) return;
    setBusy(true);
    setError('');
    try {
      await removeMyRating();
      if (showReviews) window.setTimeout(reloadReviews, 1200);
    } catch (err) {
      setError((err && err.message) || 'Could not save your rating.');
    } finally {
      setBusy(false);
    }
  };

  const requestClearRating = () => {
    if (!user || !mine?.stars || busy) return;
    if (hasWrittenReview) {
      setClearConfirmOpen(true);
      return;
    }
    performClearRating();
  };

  const pick = async (stars) => {
    if (!user) {
      setAuthMode('login');
      setAuthOpen(true);
      return;
    }
    if (stars < 1 || stars > 5) return;
    if (mine?.stars === stars) {
      requestClearRating();
      return;
    }
    setBusy(true);
    setError('');
    try {
      await saveRating(user.uid, experienceId, stars);
      if (showReviews) window.setTimeout(reloadReviews, 1200);
    } catch (err) {
      setError((err && err.message) || 'Could not save your rating.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Wrap
      $compact={compact}
      onClick={(event) => event.stopPropagation()}
      onPointerDown={(event) => event.stopPropagation()}
    >
      {!showReviews && (
        <Summary $compact={compact}>
          <span>{summary}</span>
          {count > 0 && <Count>{countLabel}</Count>}
        </Summary>
      )}
      <StarsRow>
        <Stars role="group" aria-label={user ? 'Rate this experience' : 'Sign in to rate this experience'}>
          {[1, 2, 3, 4, 5].map((n) => (
            <Star
              key={n}
              type="button"
              $compact={compact}
              $on={!!user && mine?.stars >= n}
              disabled={busy}
              aria-label={user && mine?.stars === n ? 'Remove your rating' : `${n} star${n === 1 ? '' : 's'}`}
              onClick={() => pick(n)}
            >
              {user && mine?.stars >= n ? '★' : '☆'}
            </Star>
          ))}
        </Stars>
        {user && mine?.stars >= 1 && (
          <ClearRating type="button" $compact={compact} disabled={busy} onClick={requestClearRating}>
            Clear rating
          </ClearRating>
        )}
      </StarsRow>
      {!user && (
        <SignInButton type="button" $compact={compact} onClick={() => pick(0)}>
          Sign in to rate
        </SignInButton>
      )}
      {showReviews && user && mine?.stars >= 1 && (
        <Hint>Tap the same star again to remove your rating.</Hint>
      )}
      {showReviews && user && (
        <PublicNote>
          Star ratings are anonymous. If you write a review, it will be shown with your display name.{' '}
          <a href="/privacy">Privacy Policy</a>
        </PublicNote>
      )}
      {showReviews && user && mine?.stars >= 1 && (() => {
        const showEditor = reviewEditing || (!reviewSaved && !savedReviewText.trim());
        if (!showEditor) {
          return (
            <SavedReview>
              <SavedMessage>Your review was saved.</SavedMessage>
              {savedReviewText.trim() ? <ReviewBody>{savedReviewText}</ReviewBody> : null}
              <EditReviewButton
                type="button"
                onClick={() => {
                  setDraft(savedReviewText);
                  setReviewEditing(true);
                  setReviewSaved(false);
                }}
              >
                Edit review
              </EditReviewButton>
            </SavedReview>
          );
        }
        return (
        <ReviewForm
          onSubmit={async (event) => {
            event.preventDefault();
            setBusy(true);
            setError('');
            try {
              await saveReview(user.uid, experienceId, draft);
              setConfirmedText(draft);
              setReviewSaved(true);
              setReviewEditing(false);
              setDraftReady(true);
              window.setTimeout(reloadReviews, 1200);
            } catch (err) {
              setError((err && err.message) || 'Could not save your review.');
            } finally {
              setBusy(false);
            }
          }}
        >
          <ReviewLabel htmlFor={`review-${experienceId}`}>
            {savedReviewText.trim() ? 'Edit your review' : 'Add a short review (optional)'}
          </ReviewLabel>
          <ReviewInput
            id={`review-${experienceId}`}
            maxLength={REVIEW_MAX_LENGTH}
            value={draft}
            onChange={(event) => setDraft(event.target.value.slice(0, REVIEW_MAX_LENGTH))}
          />
          <ReviewActions>
            <Counter>
              {draft.length}/{REVIEW_MAX_LENGTH}
            </Counter>
            <SaveReviewButton type="submit" disabled={busy}>
              Save
            </SaveReviewButton>
          </ReviewActions>
        </ReviewForm>
        );
      })()}
      {error && <Hint>{error}</Hint>}
      {showReviews &&
        reviews.filter((review) => typeof review.text === 'string' && review.text.trim()).slice(0, visibleCount).map((review) => {
          const text = review.text.trim();
          const stars = Math.max(0, Math.min(5, Number(review.stars) || 0));
          return (
            <ReviewCard key={review.id}>
              <ReviewMeta>
                {'★'.repeat(stars)}
                {'☆'.repeat(5 - stars)}
                {' · '}
                {review.authorName || 'RealityCheck member'}
                {formatReviewDate(review.createdAt) ? ` · ${formatReviewDate(review.createdAt)}` : ''}
                {text && reviewWasEdited(review) ? ' · edited' : ''}
              </ReviewMeta>
              {text ? <ReviewBody>{text}</ReviewBody> : null}
              {text && user?.uid === REVIEW_MODERATOR_UID && (
                <RemoveButton
                  type="button"
                  onClick={async () => {
                    setError('');
                    try {
                      await clearReview(review.id);
                      window.setTimeout(reloadReviews, 1200);
                    } catch (err) {
                      setError((err && err.message) || 'Could not remove that review.');
                    }
                  }}
                >
                  Remove text
                </RemoveButton>
              )}
            </ReviewCard>
          );
        })}
      {showReviews && reviews.filter((review) => typeof review.text === 'string' && review.text.trim()).length > visibleCount && (
        <MoreButton type="button" onClick={() => setVisibleCount((count) => count + PAGE_SIZE)}>
          Load more
        </MoreButton>
      )}
      {authOpen &&
        createPortal(
          <AuthModal
            open={authOpen}
            mode={authMode}
            onClose={() => setAuthOpen(false)}
            onModeChange={setAuthMode}
          />,
          document.body
        )}
      {clearConfirmOpen &&
        createPortal(
          <ConfirmOverlay
            onClick={(event) => event.stopPropagation()}
            onPointerDown={(event) => event.stopPropagation()}
          >
            <ConfirmCard role="dialog" aria-modal="true" aria-labelledby="clear-rating-confirm">
              <ConfirmMessage id="clear-rating-confirm">
                Clearing your rating will also delete your review.
              </ConfirmMessage>
              <ConfirmActions>
                <ConfirmCancel type="button" onClick={() => setClearConfirmOpen(false)}>
                  Cancel
                </ConfirmCancel>
                <ConfirmClear type="button" disabled={busy} onClick={performClearRating}>
                  Clear
                </ConfirmClear>
              </ConfirmActions>
            </ConfirmCard>
          </ConfirmOverlay>,
          document.body
        )}
    </Wrap>
  );
}

export default ExperienceRating;
